import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MobileNav from '../../components/feature/MobileNav';
import { supabase } from '../../lib/supabase';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  category: string;
  image: string;
  stock: number;
  rating: number;
}

export default function Products() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || 'snacks');
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [categoryParam]);

  const fetchProducts = async () => {
    const { data } = await supabase.from('products').select('*');
    if (data) setProducts(data);
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  }).sort((a, b) => a.price - b.price);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="text-gray-600">
              <i className="ri-arrow-left-line text-2xl"></i>
            </Link>
            <h1 className="text-xl font-bold text-gray-900">{t('products.title')}</h1>
            <div className="w-8"></div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder={t('products.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="px-6 pb-4 flex gap-3 overflow-x-auto">
          <button
            onClick={() => setSelectedCategory('snacks')}
            className={`px-6 py-2 rounded-full font-semibold whitespace-nowrap transition-colors ${
              selectedCategory === 'snacks'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('Snacks')}
          </button>
          <button
            onClick={() => setSelectedCategory('drinks')}
            className={`px-6 py-2 rounded-full font-semibold whitespace-nowrap transition-colors ${
              selectedCategory === 'drinks'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('Drycker')}
          </button>
          <button
            onClick={() => setSelectedCategory('icecream')}
            className={`px-6 py-2 rounded-full font-semibold whitespace-nowrap transition-colors ${
              selectedCategory === 'icecream'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('Glass')}
          </button>
          <button
            onClick={() => setSelectedCategory('uniformer')}
            className={`px-6 py-2 rounded-full font-semibold whitespace-nowrap transition-colors ${
              selectedCategory === 'uniformer'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('Uniformer')}
          </button>
        </div>
      </div>

      {/* Products Grid */}
      <div className="px-6 py-6">
        <p className="text-sm text-gray-600 mb-4">
          {filteredProducts.length} {filteredProducts.length === 1 ? t('products.productFound') : t('products.productsFound')}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop>
          {filteredProducts.map(product => (
            <Link
              key={product.id}
              to={`/product/${product.id}`}
              className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow"
            >
              <div className="relative w-full h-48">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
                {product.stock < 10 && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {t('products.lowStock')}
                  </div>
                )}
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2">{product.name}</h3>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-xl font-bold text-teal-600">{product.price} kr</div>
                  <div className="flex items-center text-xs text-gray-600">
                    <i className="ri-star-fill text-yellow-500 mr-1"></i>
                    {product.rating}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">{t('products.noProducts')}</p>
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
}